import Debug from 'debug'
import { EventEmitter } from 'events'
import { Session } from '../sessions/index.js'
import protectedSymbol from '../util/protected.js'
import APIError from '../rest/APIError.js'
import { IFrameBridge, IFrameRPC } from 'cocom'
import User from '../users/User.js'

const debug = Debug('cbio.RemoteContext')

export default class RemoteContext extends EventEmitter {
  #target

  #session

  #bridge

  #rpc

  #api

  #sockets

  #token

  constructor (target, api, sockets) {
    super()
    if (!target) throw new Error('context cannot be null')
    if (target instanceof window.HTMLIFrameElement) target = target.contentWindow
    if (!target.postMessage) throw new Error('target must have postMessage interface')

    this.#api = api
    this.#sockets = sockets
    this.#target = target
    this.#session = new Session(api, sockets)

    this.#bridge = new IFrameBridge()
    this.#bridge.trustedOrigins = [(new URL(api.baseurl)).origin]
    this.#rpc = new IFrameRPC(this.#bridge, target)
    this.#bridge.start()

    this.#bridge.on('session', this.#onSessionMessage)
    this.#bridge.on('session.metrics', this.#onSessionMetricsMessage)
    this.#bridge.on('screen', this.#onScreenMessage)
    this.#bridge.on('error', this.#onErrorMessage)

    // we need to send a message to the iframe so it can determine
    // our origin for trust purposes. We need to do this whenever
    // the iframe initialises the bridge (either delayed or immediate)
    this.#bridge.on('init', this.#sendTrust)
    this.#sendTrust()
  }

  #sendTrust = () => {
    this.#bridge.send(this.#target, 'trust', null, { trustedOnly: false })
  }

  #sendToken = () => {
    if (!this.#token) return

    this.#bridge.send(this.#target, 'token', this.#token, { trustedOnly: false, targetOrigin: this.#api.baseurl })
  }

  #onSessionMessage = (data, { trusted, origin }) => {
    if (!trusted) return console.warn('untrusted message', data, 'from', origin)
    this.#onSessionUpdated(data)
  }

  #onSessionMetricsMessage = (data, { trusted, origin }) => {
    if (!trusted) return console.warn('received untrusted message', data, 'from', origin)
    this.emit('session.metrics.updated', data)
  }

  #onErrorMessage = (data, { trusted, origin }) => {
    if (!trusted) return console.warn('received untrusted message', data, 'from', origin)
    this.emit('error', new APIError(data.id, null, data.message))
  }

  #onScreenMessage = (data, { trusted, origin }) => {
    if (!trusted) return console.warn('received untrusted message', data, 'from', origin)
    this.emit('screen.updated', data)
  }

  #onSessionUpdated = (data) => {
    const isNewSession = this.#session?.id !== data.id
    if (isNewSession) this.#session = new Session(this.#api, this.#sockets)
    this.#session[protectedSymbol].updateResource(data)

    // if the ID of the session changes then a new session has been
    // loaded into the iframe, so we should trigger a 'loaded' event
    if (isNewSession) this.emit('session.loaded', this.#session)

    // then trigger the regular update event too
    this.emit('session.updated', this.#session)
    return this.#session
  }

  setTool = async (tool) => {
    debug('set tool', tool)
    return this.#rpc.send('update:tool', { tool })
  }

  setColor = async (colour) => {
    if (this.#session.colors.includes(colour)) {
      debug('set colour', colour)
      return this.#rpc.send('update:colour', { colour })
    }

    console.warn(`Color ${colour} is not allowed by session. Accepted values are: ${this.#session.colours.join(', ')}`)
  }

  clearAnnotations = async () => {
    debug('clear annotations')
    return this.#rpc.send('update:drawing', { drawing: null })
  }

  createSession = async () => {
    debug('create session')
    const data = await this.#rpc.send('create:session', {})
    return this.#onSessionUpdated(data)
  }

  endSession = async () => {
    debug('end session')
    const data = await this.#rpc.send('update:session', { state: 'ended' })
    return this.#onSessionUpdated(data)
  }

  setFullDevice = async (state) => {
    const data = await this.#rpc.send('update:session', { full_device: state })
    return this.#onSessionUpdated(data)
  }

  setRemoteControl = async (state) => {
    const data = await this.#rpc.send('update:session', { remote_control: state })
    return this.#onSessionUpdated(data)
  }

  androidBack = async () => {
    return this.#rpc.send('create:control', { state: 'keydown', key: 'GoBack' })
  }

  androidHome = async () => {
    return this.#rpc.send('create:control', { state: 'keydown', key: 'GoHome' })
  }

  destroy = () => {
    this.#bridge.off('session', this.#onSessionMessage)
    this.#bridge.off('session.metrics', this.#onSessionMetricsMessage)
    this.#bridge.off('screen', this.#onScreenMessage)
    this.#bridge.off('error', this.#onErrorMessage)
    this.#bridge.off('init', this.#sendTrust)
    this.#bridge.off('init', this.#sendToken)
    this.#bridge.stop()
    this.removeAllListeners()
    this.#token = null
    this.#target = null
  }

  get users () {
    return {
      get: async (id) => {
        const user = await this.#rpc.send('get:user', { id })
        return new User(this.#api, user)
      },
      current: async () => {
        const user = await this.#rpc.send('get:user', { id: 'me' })
        return new User(this.#api, user)
      }
    }
  }

  [protectedSymbol] = {
    injectToken: async (token) => {
      this.#token = token
      this.#sendToken()
      this.#bridge.on('init', this.#sendToken)
    }
  }
}
